import type {
	ClipboardEvent,
	Dispatch,
	KeyboardEvent,
	SetStateAction,
} from "react"
import {
	forwardRef,
	useCallback,
	useEffect,
	useId,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconCheckOutline18 as Check,
	IconXmarkOutline18 as X,
} from "nucleo-ui-outline-18"
import { createPortal } from "react-dom"

/* eslint-disable jsx-a11y/no-static-element-interactions */
import type { EmailContact } from "@productlane/api"
import { cn } from "@productlane/lib/src/cn"
import { isValidEmailAddress } from "@productlane/lib/src/email-domain"
import {
	AutosuggestInput,
	Avatar,
	Button,
	Input,
	Tooltip,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import {
	isRecipientSeparator,
	normalizeEmailInput,
	parseRecipientInput,
} from "./parse-recipient-input"

interface EmailInputListProps {
	label: string
	recipients: EmailContact[]
	onRecipientsChange: Dispatch<SetStateAction<EmailContact[]>>
	className?: string
	rowClassName?: string
}

const recipientsIncludeEmail = (recipients: EmailContact[], email: string) =>
	recipients.some((recipient) => recipient.email === email)

const appendRecipientIfMissing = (
	recipients: EmailContact[],
	email: string,
): EmailContact[] | null => {
	if (recipientsIncludeEmail(recipients, email)) {
		return null
	}
	const newRecipient: EmailContact = { name: "", email, mailboxHash: "" }
	return [...recipients, newRecipient]
}

const appendRecipientsIfMissing = (
	recipients: EmailContact[],
	emails: string[],
): EmailContact[] | null => {
	const additions = emails
		.filter((email) => !recipientsIncludeEmail(recipients, email))
		.map((email) => ({ name: "", email, mailboxHash: "" }))
	if (additions.length === 0) return null
	return [...recipients, ...additions]
}

const EMPTY_SUGGESTIONS: string[] = []

export const EmailInputList = forwardRef<HTMLInputElement, EmailInputListProps>(
	({ label, recipients, onRecipientsChange, className, rowClassName }, ref) => {
		const fieldId = `${useId()}-${label.toLowerCase().replace(/\s+/g, "-")}`
		const hiddenSyncId = `${fieldId}-hidden-sync`

		const [query, setQuery] = useState("")
		const [debouncedQuery, setDebouncedQuery] = useState("")
		const [inputRect, setInputRect] = useState<DOMRect | null>(null)
		const [showSuggestions, setShowSuggestions] = useState(false)
		const [showAbove, setShowAbove] = useState(false)
		const [selectedIndex, setSelectedIndex] = useState(-1)
		const inputRef = useRef<HTMLInputElement>(null)
		const dropdownRef = useRef<HTMLDivElement>(null)
		const isKeyboardNavigationRef = useRef(false)

		useEffect(() => {
			const timer = setTimeout(() => {
				setDebouncedQuery(query.toLowerCase())
			}, 100)
			return () => clearTimeout(timer)
		}, [query])

		useImperativeHandle(
			ref,
			() =>
				({
					focus: () => {
						inputRef.current?.focus()
					},
					blur: () => {
						inputRef.current?.blur()
					},
				}) as HTMLInputElement,
		)

		const [contacts] = useQuery(
			queries.contactOptions({ query: debouncedQuery }),
			{ enabled: debouncedQuery.length > 0 },
		)

		const filteredContacts = useMemo(() => {
			if (!debouncedQuery) return []
			const results: typeof contacts = []
			for (const contact of contacts) {
				if (
					contact.name?.toLowerCase().includes(debouncedQuery) ||
					contact.email.toLowerCase().includes(debouncedQuery)
				) {
					results.push(contact)
					if (results.length >= 50) break
				}
			}
			return results
		}, [contacts, debouncedQuery])

		const commitTypedRecipientIfValid = useCallback(
			(typedEmail: string) => {
				const normalizedEmail = normalizeEmailInput(typedEmail)
				if (!isValidEmailAddress(normalizedEmail)) return false
				let didCommit = false
				onRecipientsChange((previousRecipients) => {
					const nextRecipients = appendRecipientIfMissing(
						previousRecipients,
						normalizedEmail,
					)
					if (!nextRecipients) return previousRecipients
					didCommit = true
					return nextRecipients
				})
				if (didCommit) setQuery("")
				return didCommit
			},
			[onRecipientsChange],
		)

		const commitRecipientList = useCallback(
			(text: string) => {
				const emails = parseRecipientInput(text)
				if (emails.length === 0) return false
				onRecipientsChange((previousRecipients) => {
					const nextRecipients = appendRecipientsIfMissing(
						previousRecipients,
						emails,
					)
					return nextRecipients ?? previousRecipients
				})
				setQuery("")
				return true
			},
			[onRecipientsChange],
		)

		const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
			const pasted = e.clipboardData.getData("text")
			if (parseRecipientInput(pasted).length < 2) return
			e.preventDefault()
			commitRecipientList(`${e.currentTarget.value}${pasted}`)
			queueMicrotask(() => inputRef.current?.focus())
		}

		const removeRecipientByEmail = (emailToRemove: string) => {
			onRecipientsChange((previousRecipients) =>
				previousRecipients.filter(
					(recipient) => recipient.email !== emailToRemove,
				),
			)
		}

		const refocusInput = () => {
			requestAnimationFrame(() => {
				inputRef.current?.focus()
			})
		}

		const toggleRecipientFromSuggestion = (suggestedEmail: string) => {
			if (!recipientsIncludeEmail(recipients, suggestedEmail)) {
				onRecipientsChange((previousRecipients) => [
					...previousRecipients,
					{ name: "", email: suggestedEmail, mailboxHash: "" },
				])
			} else {
				removeRecipientByEmail(suggestedEmail)
			}
			setQuery("")
			setShowSuggestions(false)
			setSelectedIndex(-1)
			refocusInput()
		}

		const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
			if (isRecipientSeparator(e.key)) {
				e.preventDefault()
				if (commitTypedRecipientIfValid(e.currentTarget.value)) {
					queueMicrotask(() => inputRef.current?.focus())
				}
				return
			}
			if (e.key === "Enter") {
				e.preventDefault()

				if (selectedIndex >= 0 && filteredContacts[selectedIndex]) {
					toggleRecipientFromSuggestion(filteredContacts[selectedIndex].email)
					return
				}

				if (commitTypedRecipientIfValid(e.currentTarget.value)) {
					queueMicrotask(() => inputRef.current?.focus())
				}
			} else if (e.key === "ArrowDown") {
				e.preventDefault()
				if (filteredContacts.length > 0) {
					isKeyboardNavigationRef.current = true
					setSelectedIndex((prev) =>
						prev < filteredContacts.length - 1 ? prev + 1 : 0,
					)
				}
			} else if (e.key === "ArrowUp") {
				e.preventDefault()
				if (filteredContacts.length > 0) {
					isKeyboardNavigationRef.current = true
					setSelectedIndex((prev) =>
						prev > 0 ? prev - 1 : filteredContacts.length - 1,
					)
				}
			} else if (e.key === "Escape") {
				setShowSuggestions(false)
				setSelectedIndex(-1)
			} else if (e.key === "Tab") {
				if (selectedIndex >= 0 && filteredContacts[selectedIndex]) {
					e.preventDefault()
					toggleRecipientFromSuggestion(filteredContacts[selectedIndex].email)
				}
			} else if (e.key === "Backspace") {
				if (!e.currentTarget.value.trim() && recipients.length > 0) {
					e.preventDefault()
					setShowSuggestions(false)
					setSelectedIndex(-1)
					onRecipientsChange((previousRecipients) =>
						previousRecipients.length
							? previousRecipients.slice(0, -1)
							: previousRecipients,
					)
				}
			}
		}
		const scrollToSelected = useCallback(() => {
			if (dropdownRef.current && selectedIndex >= 0) {
				const selectedElement = dropdownRef.current.children[
					selectedIndex
				] as HTMLElement
				if (selectedElement) {
					selectedElement.scrollIntoView({
						behavior: "smooth",
						block: "center",
						inline: "nearest",
					})
				}
			}
		}, [selectedIndex])

		const updateInputPosition = () => {
			if (inputRef.current) {
				const rect = inputRef.current.getBoundingClientRect()
				setInputRect(rect)

				const dropdownHeight = 144
				const spaceBelow = window.innerHeight - rect.bottom
				const spaceAbove = rect.top

				setShowAbove(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight)
			}
		}

		const handleFocus = () => {
			updateInputPosition()
			if (query.trim()) {
				setShowSuggestions(true)
			}
			setSelectedIndex(-1)
		}

		const handleBlur = () => {
			setTimeout(() => {
				setShowSuggestions(false)
				const draftEmail = inputRef.current?.value ?? ""
				commitTypedRecipientIfValid(draftEmail)
			}, 150)
		}

		useEffect(() => {
			setSelectedIndex(-1)
		}, [query])

		useEffect(() => {
			if (selectedIndex >= 0 && isKeyboardNavigationRef.current) {
				scrollToSelected()
				isKeyboardNavigationRef.current = false
			}
		}, [selectedIndex, scrollToSelected])

		useEffect(() => {
			if (!showSuggestions) return

			const handleUpdatePosition = () => {
				updateInputPosition()
			}

			window.addEventListener("scroll", handleUpdatePosition, true)
			window.addEventListener("resize", handleUpdatePosition)

			return () => {
				window.removeEventListener("scroll", handleUpdatePosition, true)
				window.removeEventListener("resize", handleUpdatePosition)
			}
		}, [showSuggestions])

		return (
			<>
				<div className={cn("", className)}>
					<div
						className={cn(
							"mb-1 ml-1.5 grid min-h-7 grid-cols-[2.25rem_minmax(0,1fr)] items-start gap-x-1.5",
							rowClassName,
						)}
					>
						<label
							htmlFor={fieldId}
							className="flex h-7 shrink-0 cursor-default select-none items-center text-left text-sm leading-none text-secondary"
						>
							{label}
						</label>
						<div className="flex min-w-0 flex-wrap content-start items-start justify-start gap-2">
							{recipients.length > 0 &&
								recipients.map((recipient) => (
									<Tooltip key={recipient.email} label={recipient.email}>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											onClick={() => removeRecipientByEmail(recipient.email)}
											className="group/chip relative -mx-1.5 h-7 w-fit min-w-0 max-w-[min(100%,16rem)] shrink-0 cursor-default justify-start overflow-hidden px-2 py-1 font-normal text-secondary"
										>
											<div className="relative min-w-0 max-w-full">
												<span
													className={cn(
														"block min-w-0 truncate",
														"group-hover/chip:[mask-image:linear-gradient(90deg,#000_0%,#000_75%,transparent_90%,transparent_100%)]",
														"group-hover/chip:[-webkit-mask-image:linear-gradient(90deg,#000_0%,#000_75%,transparent_90%,transparent_100%)]",
														"group-hover/chip:[-webkit-mask-repeat:no-repeat] group-hover/chip:[mask-repeat:no-repeat]",
													)}
												>
													{recipient.email}
												</span>
												<X
													strokeWidth={1.75}
													className="pointer-events-none absolute right-1 top-1/2 z-[2] h-3 w-3 shrink-0 -translate-y-1/2 text-secondary opacity-0 transition-opacity duration-150 group-hover/chip:opacity-100"
												/>
											</div>
										</Button>
									</Tooltip>
								))}
							<AutosuggestInput
								name={fieldId}
								suggestions={EMPTY_SUGGESTIONS}
								onSuggestionClick={toggleRecipientFromSuggestion}
								value={query}
								onChange={(e) => {
									const nextQuery = e.currentTarget.value
									setQuery(nextQuery)
									updateInputPosition()
									if (nextQuery.trim()) {
										setShowSuggestions(true)
										setSelectedIndex(-1)
									} else {
										setShowSuggestions(false)
									}
								}}
								onKeyDown={handleKeyDown}
								onPaste={handlePaste}
								onFocus={handleFocus}
								onBlur={handleBlur}
								className="min-h-0 !w-auto min-w-[8rem] max-w-full flex-1 basis-[8rem] self-start"
								inputClassName="!h-7 py-0 leading-none"
								variant="borderless"
								data-email-input={label.toLowerCase()}
								autoComplete="off"
								ref={inputRef}
							/>
						</div>
					</div>
					<Input
						id={hiddenSyncId}
						name={hiddenSyncId}
						value={query}
						className="hidden"
						autoComplete="off"
						onChange={(e) => {
							setQuery(e.currentTarget.value)
						}}
					/>
				</div>

				{!!query &&
					showSuggestions &&
					!!filteredContacts.length &&
					inputRect &&
					createPortal(
						<div
							ref={dropdownRef}
							className="fixed z-50 max-h-36 w-96 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
							style={{
								left: inputRect.left,
								top: showAbove ? inputRect.top - 4 : inputRect.bottom + 4,
								transform: showAbove ? "translateY(-100%)" : "none",
							}}
						>
							{filteredContacts.map((contactMatch, rowIndex) => (
								<div
									key={contactMatch.email}
									className={cn(
										"flex flex-row items-center justify-between border-b border-gray-100 px-3 py-2 text-sm transition-colors last:border-b-0 dark:border-gray-700",
										rowIndex === selectedIndex
											? "bg-gray-50 dark:bg-gray-900"
											: "hover:bg-gray-50 dark:hover:bg-gray-900",
									)}
									onMouseDown={(e) => {
										e.preventDefault()
										toggleRecipientFromSuggestion(contactMatch.email)
									}}
									onMouseEnter={() => {
										isKeyboardNavigationRef.current = false
										setSelectedIndex(rowIndex)
									}}
								>
									<div className="flex flex-row items-center gap-2">
										<Avatar
											name={contactMatch.name || contactMatch.email}
											image={contactMatch?.imageUrl}
											size="sm"
											roundness="full"
										/>
										<div className="flex flex-col truncate">
											<div className="max-w-64 truncate text-sm">
												{contactMatch.email}
											</div>
											<div className="max-w-48 truncate text-xs text-gray-500 dark:text-gray-400">
												{contactMatch.name}
											</div>
										</div>
									</div>
									{recipients.some(
										(recipient) => recipient.email === contactMatch.email,
									) && (
										<Check className="size-4 min-h-4 min-w-4 text-green-600" />
									)}
								</div>
							))}
						</div>,
						document.body,
					)}
			</>
		)
	},
)
